import { ExpressionSpecification } from 'mapbox-gl';

type Value = string | number | boolean;
type ValueKey = string;
type FeatureObject = Record<string, unknown>;

/**
 * Types that circularly references itself are not allowed, so we can only really do any here.
 */
export type MapExpression = ExpressionSpecification;

/**
 * equivalent to using `==` in mapbox style spec
 * @param v1
 * @param v2
 * @param collator
 */
export function equals(
  v1: MapExpression | Value,
  v2: MapExpression | Value,
  collator?: unknown
): MapExpression {
  if (collator) return ['==', v1, v2, collator];
  return ['==', v1, v2];
}

/**
 * equivalent to using `get` in mapbox style spec.
 * Retrieves a property value from the current feature's properties
 * @param  {ValueKey} v1
 * @param  {FeatureObject} obj?
 * @returns {MapExpression}
 */
export function get(v1: ValueKey, obj?: FeatureObject): MapExpression {
  if (obj) return ['get', v1, obj];
  return ['get', v1];
}

/**
 * equivalent to using `!` in mapbox style spec.
 * Returns true if the input is false, and false if the input is true.
 * @param  {MapExpression} v
 */
export function not(v: MapExpression): MapExpression {
  return ['!', v];
}

/**
 * equivalent to using `has` in mapbox style spec.
 * Tests for the presence of an property value in the current feature's properties
 * @param  {ValueKey} v1
 * @param  {FeatureObject} obj?
 */
export function has(v1: ValueKey, obj?: FeatureObject): MapExpression {
  if (obj) return ['has', v1, obj];
  return ['has', v1];
}

/**
 * equivalent to using `all` in mapbox style spec.
 * Returns true if all the inputs are true, false otherwise.
 * @param vn
 */
export function all(...vn: Array<MapExpression>): MapExpression {
  return ['all', ...vn];
}

/**
 * equivalent to using `any` in mapbox style spec.
 * Returns true if any of the inputs are true, false otherwise
 * @param vn
 */
export function any(...vn: Array<MapExpression>): MapExpression {
  return ['any', ...vn];
}

/**
 * equivalent to using `id` in mapbox style spec.
 * Gets the feature's id, if it has one.
 */
export function id(): MapExpression {
  return ['id'];
}

/**
 * equivalent to using `literal` in mapbox style spec.
 * Returns the input as a literal value. Literal values are not evaluated as expressions.
 * @param  {MapExpression} v
 */
export function literal(v: unknown): MapExpression {
  return ['literal', v];
}

/**
 * equivalent to using `case` in mapbox style spec.
 * Evaluates conditional expressions in order and returns the corresponding output for the first condition that is true.
 * If no conditions are true, it returns the fallback value.
 * @param  {Array<[MapExpression, MapExpression]>} conditionOutputPairs - An array of condition-output pairs.
 * @param  {MapExpression} fallback - The value to return if no conditions are true.
 * @returns {MapExpression}
 */
export function caseExpression(
  conditionOutputPairs: Array<[MapExpression, MapExpression]>,
  fallback: MapExpression
): MapExpression {
  // The initial 'case' keyword and the fallback
  const expression: MapExpression = ['case'];

  // Add each condition-output pair to the expression array
  conditionOutputPairs.forEach(([condition, output]) => {
    expression.push(condition, output);
  });

  // Add the fallback value as the last argument
  expression.push(fallback);

  return expression;
}
